import { Link } from 'react-router-dom';
import GuideLayout, { type TocItem, type RelatedCalculator } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'overview', label: 'Overview', level: 1 },
  { id: 'ingestion', label: 'Data Ingestion Pricing', level: 1 },
  { id: 'sentinel', label: 'Microsoft Sentinel Add-on', level: 1 },
  { id: 'retention', label: 'Data Retention Pricing', level: 1 },
  { id: 'search-jobs', label: 'Search Jobs', level: 1 },
  { id: 'restore-trap', label: 'The 2TB Restore Trap', level: 1 },
  { id: 'log-types', label: 'Basic vs Analytics Logs', level: 1 },
  { id: 'certifications', label: 'Certification Resources', level: 1 },
  { id: 'resources', label: 'Official Resources', level: 1 },
];

const relatedCalculators: RelatedCalculator[] = [
  { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics & Sentinel Calculator' },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Azure Log Analytics Pricing UK 2026',
  description:
    'Complete guide to Log Analytics costs in UK South — ingestion, retention, search jobs, and the 2TB restore minimum explained in GBP.',
  author: { '@type': 'Organization', name: 'AzureCalc.uk' },
  publisher: {
    '@type': 'Organization',
    name: 'AzureCalc.uk',
    url: 'https://www.azure-calc.co.uk',
  },
  dateModified: '2026-03-30',
  url: 'https://www.azure-calc.co.uk/guides/azure-log-analytics-pricing-uk',
};

export default function LogAnalyticsPricingUKPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <GuideLayout
        title="Azure Log Analytics Pricing UK 2026"
        description="Complete guide to Log Analytics costs in UK South — ingestion, retention, search jobs, and the 2TB restore minimum explained in GBP."
        lastUpdated="March 2026"
        tocItems={tocItems}
        relatedCalculators={relatedCalculators}
        bottomCta={
          <AffiliateCTA
            programme="MeasureUp"
            courseName="SC-200 Official Practice Test"
            url="https://www.measureup.com/microsoft-practice-test-sc-200-microsoft-security-operations-analyst.html"
            price="From £99"
          />
        }
        canonical="https://www.azure-calc.co.uk/guides/azure-log-analytics-pricing-uk"
        metaTitle="Azure Log Analytics Pricing UK 2026 | AzureCalc.uk"
        metaDescription="Azure Log Analytics costs UK South 2026 in GBP. Ingestion tiers, Sentinel add-on, retention, search jobs, and the 2TB restore minimum explained."
      >
        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* ── Section 1: Overview ── */}
        <section id="overview">
          <h2>Overview</h2>
          <p>
            If you restore 1 GB of archived Log Analytics data, you are billed for 2,000 GB.
            That is not a typo. The Restore operation has a 2 TB minimum billing floor
            regardless of how much data you actually request. It is one of the most expensive
            accidental triggers in Azure, and it is buried in the pricing documentation under
            a footnote. If your team runs any Restore operation without knowing this, the result
            is a surprise line item in the hundreds of pounds range.
          </p>
          <p>
            Azure Log Analytics is the foundation of monitoring on Azure. Every Azure resource —
            virtual machines, App Services, Kubernetes clusters, Azure Firewall, Microsoft Sentinel —
            can send its logs and metrics to a Log Analytics workspace for storage, querying, and
            alerting. If you run anything on Azure, you are almost certainly incurring Log Analytics
            costs whether you have looked at the bill or not.
          </p>
          <p>
            The challenge is that Log Analytics pricing has <strong>six distinct dimensions</strong>{' '}
            that interact with each other: data ingestion tier, Sentinel add-on, interactive
            retention, archive retention, Search Job costs, and Restore costs. Most engineers are
            aware of ingestion costs but are blindsided by retention charges on large workspaces or
            — most dangerously — the 2TB minimum billing floor on Restore operations.
          </p>
          <p>
            This guide covers all six dimensions with UK South GBP pricing sourced directly from
            the Azure Retail Prices API. All figures shown are current as of March 2026 and reflect
            the prices used by the{' '}
            <a
              href="/log-analytics"
              className="text-[#0078d4] hover:underline transition-colors"
            >
              Log Analytics calculator
            </a>{' '}
            on this site.
          </p>
        </section>

        {/* ── Section 2: Data Ingestion Pricing ── */}
        <section id="ingestion">
          <h2>Data Ingestion Pricing</h2>
          <p>
            Ingestion is the dominant cost driver for most workspaces. When data lands in your Log
            Analytics workspace, it is classified into one of three log types — and the type
            determines both the ingestion price and what you can do with the data afterwards.
          </p>

          <h3>The three log types</h3>

          {/* Log type cards */}
          <div className="not-prose grid grid-cols-1 gap-3 mb-6">
            {[
              {
                type: 'Analytics Logs',
                price: '£2.13/GB',
                colour: 'border-[#0078d4]',
                badge: 'Default',
                badgeColour: 'bg-[#0078d4] text-white',
                features: [
                  'First 5 GB/day per workspace is FREE',
                  'Full KQL query capability — no per-query charge',
                  'Alert rules, workbooks, dashboards fully supported',
                  'Default retention: 31 days free, up to 730 days',
                  'Best for: security logs, SigninLogs, SecurityEvent, AuditLogs',
                ],
              },
              {
                type: 'Basic Logs',
                price: '£0.46/GB',
                colour: 'border-[#604878]',
                badge: '78% cheaper ingestion',
                badgeColour: 'bg-[#604878] text-white',
                features: [
                  'Query costs £0.0046/GB scanned (charged on query)',
                  '8-day interactive retention only (cannot be extended)',
                  'No alert rules — cannot set up scheduled alerts',
                  'Best for: verbose logs queried rarely (AppServiceHTTPLogs, NetworkFlow)',
                ],
              },
              {
                type: 'Auxiliary Logs',
                price: '£0.04/GB',
                colour: 'border-[#107c10]',
                badge: 'Cheapest',
                badgeColour: 'bg-[#107c10] text-white',
                features: [
                  'Query via Search Jobs only — no direct KQL',
                  '30-day interactive retention, up to 12 years archive',
                  'No alert rules, no workbook support',
                  'Best for: compliance logs archived but rarely investigated',
                  'New: available from August 2025',
                ],
              },
            ].map(({ type, price, colour, badge, badgeColour, features }) => (
              <div
                key={type}
                className={`border-l-2 ${colour} border-t border-r border-b rounded-lg p-5`}
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>{type}</h4>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${badgeColour}`}>
                      {badge}
                    </span>
                  </div>
                  <span className="text-[18px] font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{price}</span>
                </div>
                <ul className="flex flex-col gap-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3>Comparison table</h3>
          <table>
            <thead>
              <tr>
                <th>Log Type</th>
                <th>Ingestion cost</th>
                <th>Query cost</th>
                <th>Best for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Analytics Logs</strong></td>
                <td>£2.13/GB (first 5 GB/day free)</td>
                <td>Free</td>
                <td>Security logs, anything with alert rules</td>
              </tr>
              <tr>
                <td><strong>Basic Logs</strong></td>
                <td>£0.46/GB</td>
                <td>£0.0046/GB scanned</td>
                <td>Verbose operational logs queried rarely</td>
              </tr>
              <tr>
                <td><strong>Auxiliary Logs</strong></td>
                <td>£0.04/GB</td>
                <td>Via Search Jobs only</td>
                <td>Compliance archival, almost never queried</td>
              </tr>
            </tbody>
          </table>

          <h3>PAYG vs Commitment tiers</h3>
          <p>
            If your workspace ingests more than approximately <strong>90 GB/day</strong> of
            Analytics Logs, a commitment tier becomes cheaper than PAYG.
          </p>

          <div className="not-prose border rounded-lg overflow-hidden mb-6" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-container-low)' }}>
                  <th className="text-left font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Tier</th>
                  <th className="text-right font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Daily price</th>
                  <th className="text-right font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Effective per GB</th>
                  <th className="text-right font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Saving vs PAYG</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { tier: 'PAYG (above 5 GB/day)', daily: '—', perGb: '£2.13/GB', saving: '—' },
                  { tier: '100 GB/day commitment', daily: '£181.02', perGb: '£1.81/GB', saving: '15%' },
                  { tier: '200 GB/day commitment', daily: '£339.87', perGb: '£1.70/GB', saving: '20%' },
                  { tier: '300 GB/day commitment', daily: '£498.73', perGb: '£1.66/GB', saving: '22%' },
                  { tier: '400 GB/day commitment', daily: '£650.19', perGb: '£1.63/GB', saving: '23%' },
                  { tier: '500 GB/day commitment', daily: '£798.88', perGb: '£1.60/GB', saving: '25%' },
                ].map(({ tier, daily, perGb, saving }) => (
                  <tr key={tier} style={{ borderBottom: '1px solid var(--surface-container-high)' }} className="last:border-0">
                    <td className="px-4 py-2.5" style={{ color: 'var(--text-secondary)' }}>{tier}</td>
                    <td className="px-4 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>{daily}</td>
                    <td className="px-4 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>{perGb}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#107c10] font-semibold">{saving}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="not-prose flex items-start gap-3 border rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'rgba(0, 120, 212, 0.4)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0 mt-0.5">lightbulb</span>
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
              <strong style={{ color: '#c7e0f4' }}>Break-even guidance:</strong> PAYG becomes more
              expensive than the 100 GB/day commitment tier when you exceed approximately{' '}
              <strong style={{ color: '#c7e0f4' }}>90 GB/day</strong> of billable ingestion. If your
              workspace consistently ingests 80–100 GB/day, run the numbers — switching to a
              commitment tier could save £1,000–£3,000/month.
            </p>
          </div>

          <div className="not-prose mb-6">
            <CalculatorEmbed service="log-analytics" />
          </div>
        </section>

        {/* ── Section 3: Sentinel Add-on ── */}
        <section id="sentinel">
          <h2>Microsoft Sentinel Add-on</h2>
          <p>
            Microsoft Sentinel is billed as an add-on on top of Log Analytics. When you enable
            Sentinel on a workspace, every GB ingested into Analytics Logs tables incurs an
            additional Sentinel charge on top of the Log Analytics ingestion cost.
          </p>

          <div className="not-prose border rounded-lg overflow-hidden mb-6" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-container-low)' }}>
                  <th className="text-left font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Tier</th>
                  <th className="text-right font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Sentinel charge</th>
                  <th className="text-right font-semibold px-4 py-2.5" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Combined total</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { tier: 'PAYG', sentinel: '+£3.975/GB', combined: '≈ £6.10/GB' },
                  { tier: '100 GB/day Sentinel commitment', sentinel: '£273.38/day', combined: '+ LA costs separately' },
                  { tier: '500 GB/day Sentinel commitment', sentinel: '£1,168.31/day', combined: '+ LA costs separately' },
                ].map(({ tier, sentinel, combined }) => (
                  <tr key={tier} style={{ borderBottom: '1px solid var(--surface-container-high)' }} className="last:border-0">
                    <td className="px-4 py-2.5" style={{ color: 'var(--text-secondary)' }}>{tier}</td>
                    <td className="px-4 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>{sentinel}</td>
                    <td className="px-4 py-2.5 text-right font-mono" style={{ color: 'var(--text-tertiary)' }}>{combined}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="not-prose flex items-start gap-3 border rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0 mt-0.5">info</span>
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
              <strong style={{ color: '#c7e0f4' }}>Independent commitment tiers:</strong> Sentinel
              and Log Analytics have separate commitment tier programmes. You can be on PAYG for Log
              Analytics and a commitment tier for Sentinel, or vice versa. Optimise each
              independently based on your actual volumes.
            </p>
          </div>

          <h3>Free data allowances that reduce Sentinel costs</h3>
          <div className="not-prose flex flex-col gap-2 mb-4">
            {[
              { licence: 'Microsoft 365 E5', allowance: '5 MB per user per day free into Sentinel' },
              { licence: 'Defender for Servers Plan 2', allowance: '500 MB per server per day free into Sentinel' },
              { licence: 'First 31-day trial', allowance: 'Up to 10 GB/day free for new Sentinel workspaces' },
            ].map(({ licence, allowance }) => (
              <div key={licence} className="flex items-start gap-3 rounded p-3" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">check_circle</span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: '#c7e0f4' }}>{licence}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{allowance}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 4: Retention Pricing ── */}
        <section id="retention">
          <h2>Data Retention Pricing</h2>
          <p>
            After data is ingested, you are charged for how long you keep it. Retention pricing has
            two distinct phases: <strong>interactive retention</strong> (data is hot, fully
            queryable) and <strong>archive retention</strong> (data is cold, queryable only via
            Search Jobs or Restore).
          </p>

          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[
              {
                label: 'Interactive Retention',
                icon: 'search',
                colour: 'border-[#0078d4]',
                items: [
                  'First 31 days: FREE (all workspaces)',
                  'First 90 days: FREE if Sentinel is enabled',
                  'Days 32–730: £0.0961/GB/month',
                  'Full KQL, alerts, workbooks supported',
                  'Maximum: 730 days',
                ],
              },
              {
                label: 'Archive Retention',
                icon: 'archive',
                colour: 'border-[#604878]',
                items: [
                  'After interactive period ends',
                  'Up to 12 years total retention',
                  '£0.0185/GB/month — significantly cheaper',
                  'Query via Search Jobs or Restore only',
                  'No direct KQL, no alert rules',
                ],
              },
            ].map(({ label, icon, colour, items }) => (
              <div key={label} className={`border-l-2 ${colour} border-t border-r border-b rounded-lg p-5`} style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-[#0078d4] text-[18px]">{icon}</span>
                  <h4 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>{label}</h4>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3>Practical guidance</h3>
          <ul>
            <li>
              <strong>Most teams set interactive retention to 90 days.</strong> This covers the
              majority of incident investigation scenarios — the median time to discover a breach
              is under 30 days, and 90 days of interactive data is sufficient for most forensic
              work.
            </li>
            <li>
              Archive beyond 90 days for regulatory compliance (PCI-DSS, FCA record keeping
              requirements) at the cheaper archive rate of £0.0185/GB/month.
            </li>
            <li>
              <strong>Do not set long interactive retention for all tables.</strong> If you have a
              100 GB/day workspace and extend interactive retention to 365 days, the retention
              costs on days 32–365 can exceed your ingestion costs. Target long interactive
              retention only at high-value tables (SecurityEvent, SigninLogs) and keep verbose
              tables shorter.
            </li>
          </ul>
        </section>

        {/* ── Section 5: Search Jobs ── */}
        <section id="search-jobs">
          <h2>Search Jobs — The Right Way to Query Archive Data</h2>
          <p>
            A Search Job is a background operation that scans your archived (or Basic Logs) data
            and writes the matching results to a temporary <code>_SRCH</code> table in your
            workspace. You then query that table interactively at no extra charge. Search Jobs are
            the cost-effective default for ad-hoc investigation of old data.
          </p>

          <div className="not-prose border rounded-lg p-5 mb-6" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>Search Job pricing</h4>
              <span className="text-[20px] font-bold text-[#107c10] font-mono">£0.0046/GB scanned</span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {[
                'No minimum charge — you pay only for GB scanned',
                'Results written to a temporary _SRCH table (free to query)',
                'Runs asynchronously — results available in minutes to hours',
                'Can run against Analytics, Basic, and Auxiliary Logs',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="text-[#107c10] shrink-0 mt-0.5">›</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <h3>Worked example</h3>
          <div className="not-prose rounded-lg p-5 mb-6 font-mono text-[13px]" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.3)' }}>
            <p className="mb-3" style={{ color: 'var(--text-tertiary)' }}>Scenario: investigate 30 days of archived logs from a table ingesting 35 GB/day</p>
            <div className="flex flex-col gap-1.5">
              {[
                ['GB scanned', '35 GB/day × 30 days =', '1,050 GB'],
                ['Search Job cost', '1,050 × £0.0046 =', '£4.83'],
                [''],
                ['Compare — Restore for same data', '', ''],
                ['Restore cost (2TB minimum applies)', '2,048 GB × £0.0924/day =', '£189.24/day'],
                ['3-day restore', '', '£567.71'],
              ].map((row, i) =>
                row[0] === '' ? (
                  <hr key={i} style={{ borderColor: 'var(--outline-variant)' }} className="my-2" />
                ) : (
                  <div key={i} className="flex justify-between items-baseline gap-4">
                    <span style={{ color: 'var(--text-tertiary)' }}>{row[0]}</span>
                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{row[1]}</span>
                    <span className={`font-bold ${row[2]?.startsWith('£') && parseFloat(row[2].replace(/[£,]/g,'')) > 100 ? 'text-[#e8b000]' : 'text-[#107c10]'}`}>
                      {row[2]}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>

          <p>
            The same 1,050 GB of data costs <strong>£4.83 via Search Job</strong> compared to
            a minimum of <strong>£567.71 via Restore</strong> (3 days). Unless you need to run
            many repeated KQL queries against the same dataset, Search Jobs are almost always the
            right choice.
          </p>
        </section>

        {/* ── Section 6: 2TB Restore Trap ── */}
        <section id="restore-trap">
          <h2>The 2TB Restore Trap — Read This Before You Restore Anything</h2>

          <div className="not-prose bg-[#a4262c]/10 border-2 border-[#a4262c]/50 rounded-lg p-5 mb-6">
            <div className="flex items-start gap-3">
              <span
                className="material-symbols-outlined text-red-400 text-[28px] shrink-0"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                warning
              </span>
              <div>
                <p className="text-[14px] font-bold text-red-400 mb-2">
                  2TB MINIMUM BILLING — EVERY RESTORE OPERATION
                </p>
                <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  Azure bills a minimum of <strong>2TB (2,048 GB)</strong>{' '}
                  per Restore operation, regardless of how much data you actually restore.
                  Restoring 3 GB costs exactly the same as restoring 2,048 GB. This catches
                  many engineers completely off guard — a "quick restore" of a small table can
                  result in a bill of hundreds of pounds.
                </p>
              </div>
            </div>
          </div>

          <p>
            Restore brings archived data into a hot cache for repeated interactive querying.
            The cost is <strong>£0.0924/GB/day</strong>, with a minimum billing period of 12
            hours. Both the 2TB floor and the 12-hour minimum apply even if you delete the
            restore table immediately after creation.
          </p>

          <h3>Worked example — the 3 GB restore mistake</h3>
          <div className="not-prose rounded-lg p-5 mb-6 font-mono text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <p className="mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Scenario: restore 3 GB of OfficeActivity logs for a 3-day investigation
            </p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Data you wanted to restore', value: '3 GB', highlight: false },
                { label: 'Data Azure bills for (2TB minimum)', value: '2,048 GB', highlight: true },
                { label: 'Cost per day', value: '2,048 × £0.0924 = £189.24/day', highlight: false },
                { label: '3-day restore total', value: '£567.71', highlight: true },
                { label: '', value: '' , highlight: false },
                { label: 'Same data via Search Job', value: '3 × £0.0046 = £0.014', highlight: false },
              ].map(({ label, value, highlight }, i) =>
                label === '' ? (
                  <hr key={i} style={{ borderColor: 'var(--outline-variant)' }} className="my-1" />
                ) : (
                  <div key={i} className="flex justify-between items-baseline gap-4">
                    <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
                    <span className={`font-bold ${highlight ? 'text-red-400' : ''}`} style={highlight ? {} : { color: 'var(--text-secondary)' }}>{value}</span>
                  </div>
                )
              )}
            </div>
          </div>

          <h3>When Restore IS worth it</h3>
          <ul>
            <li>
              <strong>Your restore is actually over 2TB.</strong> If you genuinely need to restore
              2+ TB of data, the 2TB floor no longer penalises you.
            </li>
            <li>
              <strong>You need to run dozens of KQL queries against the same dataset.</strong>{' '}
              Restore gives you full interactive query speed for a sustained investigation. If you
              are running 50+ queries against the same archived data over several days, the fixed
              daily cost may be lower than running 50 Search Jobs.
            </li>
            <li>
              <strong>You need the full 10-minute query timeout.</strong> Search Jobs have
              execution constraints; Restore gives you full KQL performance for complex analytics.
            </li>
          </ul>

          <div className="not-prose mb-6">
            <CalculatorEmbed service="log-analytics" />
          </div>

          <div className="not-prose">
            <ResourceLink
              title="Restore logs in Azure Monitor — 2TB minimum"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/restore"
              source="Microsoft Learn"
            />
          </div>
        </section>

        {/* ── Section 7: Basic vs Analytics Logs ── */}
        <section id="log-types">
          <h2>Basic vs Analytics Logs — Which Should You Use?</h2>
          <p>
            Switching high-volume tables from Analytics Logs to Basic Logs is one of the highest-
            impact cost optimisations available in Log Analytics — but it comes with trade-offs
            that must be understood before making the change.
          </p>

          <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              {
                type: 'Use Analytics Logs when',
                icon: 'analytics',
                colour: 'border-[#0078d4]',
                items: [
                  'You query this table regularly (more than weekly)',
                  'You have alert rules on this table',
                  'Workbooks or dashboards reference it',
                  'It is a security table: SecurityEvent, SigninLogs, AuditLogs, OfficeActivity',
                  'You need sub-second query response times',
                ],
              },
              {
                type: 'Use Basic Logs when',
                icon: 'sort',
                colour: 'border-[#604878]',
                items: [
                  'Verbose operational logs queried less than once per week',
                  'No alert rules are needed on this table',
                  'Examples: AppServiceHTTPLogs, NetworkFlow, ContainerLog',
                  'You are willing to pay £0.0046/GB when you do query it',
                ],
              },
              {
                type: 'Use Auxiliary Logs when',
                icon: 'archive',
                colour: 'border-[#107c10]',
                items: [
                  'Pure compliance archival — logs you are legally required to keep',
                  'Rarely if ever need to investigate this data',
                  'Maximum cost reduction is the priority',
                  'Available from August 2025 onwards',
                ],
              },
            ].map(({ type, icon, colour, items }) => (
              <div
                key={type}
                className={`border-l-2 ${colour} rounded-lg p-4`}
                style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-[#0078d4] text-[18px]">{icon}</span>
                  <h4 className="text-[13px] font-bold leading-snug" style={{ color: '#c7e0f4' }}>{type}</h4>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3>Cost saving example</h3>
          <div className="not-prose border rounded-lg p-5 mb-4 font-mono text-[13px]" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="mb-3 font-sans text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              Scenario: 100 GB/day of <code>AppServiceHTTPLogs</code> — switching from Analytics to Basic
            </p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Analytics Logs', value: '100 GB × £2.13 × 30 days = £6,390/month', highlight: false },
                { label: 'Basic Logs', value: '100 GB × £0.46 × 30 days = £1,380/month', highlight: true },
                { label: 'Monthly saving', value: '≈ £5,010/month', highlight: true },
              ].map(({ label, value, highlight }) => (
                <div key={label} className="flex justify-between items-baseline gap-4">
                  <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
                  <span className={`font-bold ${highlight ? 'text-[#107c10]' : ''}`} style={highlight ? {} : { color: 'var(--text-secondary)' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          <p>
            The saving is significant — but only applies to tables where you do not need alert rules
            and are comfortable paying per-query. Never switch a table to Basic Logs without first
            checking whether any alert rules or workbooks depend on it.
          </p>
        </section>

        {/* ── Section 8: Certifications ── */}
        <section id="certifications">
          <h2>Certification Resources</h2>
          <p>
            Want to understand Azure Monitor and Sentinel pricing in depth for your career? Both
            the <strong>SC-200</strong> (Security Operations Analyst) and{' '}
            <strong>AZ-104</strong> (Azure Administrator) certifications cover Log Analytics
            extensively. SC-200 is the deeper of the two — Domain 3 covers Sentinel workspace
            architecture, data connectors, KQL, and cost management in detail.
          </p>

          <div className="not-prose flex flex-col gap-4">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="SC-200 Microsoft Security Operations Analyst"
              url="https://www.pluralsight.com/paths/sc-200-microsoft-security-operations-analyst-associate"
              price="From £29/month"
              badgeText="Covers Sentinel & Log Analytics"
            />
            <AffiliateCTA
              programme="MeasureUp"
              courseName="SC-200 Official Practice Test"
              url="https://www.measureup.com/microsoft-practice-test-sc-200-microsoft-security-operations-analyst.html"
              price="From £99"
            />
          </div>
        </section>

        {/* ── Section 9: Official Resources ── */}
        <section id="resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-3">
            <ResourceLink
              title="Azure Monitor pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/monitor/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Log Analytics cost calculations"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/cost-logs"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Restore logs documentation"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/restore"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Basic Logs configuration"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/basic-logs-configure"
              source="Microsoft Learn"
            />
          </div>
        </section>

        {/* Related certifications */}
        <section>
          <h2>Related Certifications</h2>
          <p>These certifications cover the Log Analytics and Sentinel topics explained in this guide:</p>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
            <Link
              to="/certifications/sc-200-study-guide-uk"
              className="group flex items-center gap-4 px-4 py-4 rounded transition-colors"
              style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.5)' }}
            >
              <span className="material-symbols-outlined text-[#0078d4] text-[24px] shrink-0">workspace_premium</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold transition-colors group-hover:text-white" style={{ color: 'var(--text-primary)' }}>SC-200 Study Guide UK</p>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Covers Sentinel, KQL, Log Analytics in depth</p>
              </div>
              <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0">arrow_forward</span>
            </Link>
            <Link
              to="/certifications/az-104-study-guide-uk"
              className="group flex items-center gap-4 px-4 py-4 rounded transition-colors"
              style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.5)' }}
            >
              <span className="material-symbols-outlined text-[#0078d4] text-[24px] shrink-0">workspace_premium</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold transition-colors group-hover:text-white" style={{ color: 'var(--text-primary)' }}>AZ-104 Study Guide UK</p>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Domain 5 covers Azure Monitor and Log Analytics</p>
              </div>
              <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0">arrow_forward</span>
            </Link>
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
